import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { Alert, Box, Button, Paper, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

/**
 * TeamCheckTop — page PUBLIQUE (même lien/jeton que la validation des
 * inscriptions) : par catégorie, les 15 meilleurs aux points (groupe du
 * tirage au sort), puis le 16e et le 17e, sur la base des inscriptions
 * actuelles. Ajouté le 01.10.2026.
 */
export default function TeamCheckTop() {
    const { eventKey } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get("t") || "";
    const [data, setData] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        fetch(`/api/public/team-check/${encodeURIComponent(eventKey)}/top?t=${encodeURIComponent(token)}`)
            .then(async (r) => {
                const d = await r.json().catch(() => ({}));
                if (!r.ok) throw new Error(d.error || `Error ${r.status}`);
                setData(d);
            })
            .catch((err) => setError(err.message));
    }, [eventKey, token]);

    return (
        <Box sx={{ p: 2, maxWidth: 760, mx: "auto" }}>
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(`/team-check/${encodeURIComponent(eventKey)}?t=${encodeURIComponent(token)}`)}
                sx={{ mb: 1 }}
            >
                Back to entries
            </Button>
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                Best by points
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Top 15 (seeding draw) followed by 16th and 17th, based on the current entries. Athletes without points are
                not listed here.
            </Typography>

            {error && <Alert severity="error">{error}</Alert>}

            {(data?.categories || []).map((c) => (
                <Paper key={c.category} variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Typography variant="h6">
                        {c.category}{" "}
                        <Typography component="span" variant="body2" color="text.secondary">
                            ({c.total} entered{c.withoutPoints ? `, ${c.withoutPoints} without points` : ""})
                        </Typography>
                    </Typography>
                    {c.top.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            No athlete with points.
                        </Typography>
                    ) : (
                        // Liste compacte (lisible sur téléphone) : rang, nom sur
                        // une ligne, année + club en dessous, points à droite.
                        <Box sx={{ mt: 1 }}>
                            {c.top.map((a) => {
                                const extra = a.position > 15;
                                return (
                                    <React.Fragment key={`${a.fiscode}-${a.position}`}>
                                        {a.position === 16 && (
                                            <Typography
                                                variant="caption"
                                                sx={{ display: "block", mt: 1, mb: 0.5, px: 1, py: 0.5, bgcolor: "action.hover", fontWeight: "bold", borderRadius: 1 }}
                                            >
                                                Next (16th, 17th)
                                            </Typography>
                                        )}
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1.5,
                                                py: 0.75,
                                                borderBottom: "1px solid",
                                                borderColor: "divider",
                                                opacity: extra ? 0.75 : 1,
                                            }}
                                        >
                                            <Typography sx={{ width: 28, textAlign: "right", fontWeight: "bold", flexShrink: 0 }}>{a.position}</Typography>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography noWrap sx={{ fontWeight: extra ? "normal" : "bold", fontStyle: extra ? "italic" : "normal" }}>
                                                    {a.lastname} {a.firstname}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" noWrap component="div">
                                                    {[a.birthyear, a.nation, a.club, a.association || a.inscription].filter(Boolean).join(" · ")}
                                                </Typography>
                                            </Box>
                                            <Typography sx={{ fontFamily: "monospace", fontWeight: "bold", flexShrink: 0 }}>
                                                {Number(a.pts).toFixed(2)}
                                            </Typography>
                                        </Box>
                                    </React.Fragment>
                                );
                            })}
                        </Box>
                    )}
                </Paper>
            ))}
        </Box>
    );
}